<template>
  <print-view v-if="isPrint" :form="form" :board="board" />
  <div v-else>
    <!-- 기본정보 (숨김 - 데이터 유지용) -->
    <field-select-module v-show="false" v-model="form.stdInfo" :board="board" :is-print="isPrint" />

    <!-- 유효성 검사 메시지 -->
    <div v-if="validateMsg" class="validate-msg mb-4" style="justify-content: flex-end">
      <span class="validate-msg-icon">!</span>
      {{ validateMsg }}
    </div>

    <!-- 관리감독자 정보 -->
    <div>
      <h5 class="mb-4"><i class="dot"></i> 관리감독자 정보</h5>
      <div v-if="supervisorNotFound && isNew && !board.readonly && !isPrint" class="validate-msg mb-4" style="justify-content: flex-start">
        <span class="validate-msg-icon">!</span>
        관리감독자 정보를 확인할 수 없습니다. 관리감독자 정보를 불러와 주세요.
      </div>
      <div class="writeGridContainer">
        <div>
          <div>관리감독자 현황</div>
          <vs-button v-if="!board.readonly && !isPrint" color="secondary" @click="showPopup = true">관리감독자 정보 불러오기</vs-button>
        </div>
      </div>
      <div class="writeGridContainer">
        <div>
          <div>사업장명</div>
          <p v-if="isPrint" class="print-input">{{ form.관리감독자정보.사업장명 }}</p>
          <ml-input v-else v-model="form.관리감독자정보.사업장명" :readonly="true" />
        </div>
        <div>
          <div>부서명</div>
          <p v-if="isPrint" class="print-input">{{ form.관리감독자정보.부서명 }}</p>
          <ml-input v-else v-model="form.관리감독자정보.부서명" :readonly="true" />
        </div>
        <div>
          <div>직무</div>
          <p v-if="isPrint" class="print-input">{{ form.관리감독자정보.직무 }}</p>
          <ml-input v-else v-model="form.관리감독자정보.직무" :readonly="true" />
        </div>
        <div>
          <div>직급</div>
          <p v-if="isPrint" class="print-input">{{ form.관리감독자정보.직급 }}</p>
          <ml-input v-else v-model="form.관리감독자정보.직급" :readonly="true" />
        </div>
        <div>
          <div>이름</div>
          <p v-if="isPrint" class="print-input">{{ form.관리감독자정보.이름 }}</p>
          <ml-input v-else v-model="form.관리감독자정보.이름" :readonly="true" />
        </div>
        <div>
          <div>서명</div>
          <sign-field v-model="form.관리감독자정보.서명" :readonly="board.readonly || !isOwnRecord" />
        </div>
      </div>
    </div>

    <!-- 점검 정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 점검 정보</h5>
      <div class="writeGridContainer">
        <div>
          <div>점검일</div>
          <p v-if="isPrint" class="print-input">{{ form.점검일 }}</p>
          <date-selector v-else v-model="form.점검일" :readonly="board.readonly" />
        </div>
        <div>
          <div>점검시간</div>
          <p v-if="isPrint" class="print-input">{{ form.점검시간 }}</p>
          <time-selector v-else v-model="form.점검시간" :readonly="board.readonly" />
        </div>
        <div>
          <div>작성일</div>
          <p v-if="isPrint" class="print-input">{{ form.작성일 }}</p>
          <date-selector v-else v-model="form.작성일" :readonly="true" />
        </div>
      </div>
    </div>

    <!-- 관리감독자 점검 항목 -->
    <table-inspect-merge
      class="mt-28px"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      tableHeaderTitle="점검 항목"
      :tableHeaderCheck="[{ text: '양호' }, { text: '불량' }, { text: '해당없음' }]"
      :tableHeaderNote="[{ text: '비고(조치사항 기재)' }]"
      requiredNoteCheck="불량"
      collapsible
      :initial-expanded="isNew"
      v-model="form.관리감독자점검항목"
    >
      <template #title>
        <h5><i class="dot"></i> 관리감독자 점검 항목</h5>
        <span class="ml-4" style="font-size: 13px; color: #666">비상 연락망 : 안전환경팀 스마트 통합관제지원실 (054-721-3555)</span>
      </template>
    </table-inspect-merge>

    <!-- 작업시작전 점검사항 -->
    <table-inspect-merge
      class="mt-28px"
      title="작업시작전 점검사항"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      tableHeaderTitle="점검내용"
      :tableHeaderCheck="[{ text: '양호' }, { text: '불량' }, { text: '해당없음' }]"
      :tableHeaderNote="[{ text: '비고(조치사항 기재)' }]"
      requiredNoteCheck="불량"
      collapsible
      v-model="form.작업시작전점검사항"
    />

    <!-- 사진대지 -->
    <photo-upload-grid-module :row-count="2" :grid-count="2" v-model="form.photoList" :is-print="isPrint" :readonly="board.readonly" section-title="사진대지" placeholder="점검 현장 사진" class="mt-28px" />

    <!-- 파일첨부 -->
    <file-upload-module :readonly="board.readonly" v-model="form.fileList" :is-print="isPrint" class="mt-8" />

    <!-- 관리감독자 정보 불러오기 팝업 -->
    <popup-load-prev-board title="관리감독자 정보 불러오기" :set-board-id="738" v-model="showPopup" @selectRowData="onSelectManager" />
  </div>
</template>

<script>
import PrintView from "./Print.vue";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import TableInspectMerge from "@/components/TableInspectMerge.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import SignField from "@/components/SignField.vue";
import PopupLoadPrevBoard from "@/popup/PopupLoadPrevBoard.vue";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import { 관리감독자점검항목, 작업시작전점검사항 } from "./inspectData";

export default {
  components: {
    PrintView,
    PopupLoadPrevBoard,
    SignField,
    TimeSelector,
    DateSelector,
    TableInspectMerge,
    FileUploadModule,
    FieldSelectModule,
    PhotoUploadGridModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  computed: {
    isNew() {
      return !this.$route.params.post_id;
    },
    isOwnRecord() {
      const userInfo = this.$store.state.auth.userInfo;
      if (!userInfo || !userInfo.nickname) return false;
      return this.form.관리감독자정보.사번 === userInfo.nickname;
    },
  },
  data() {
    return {
      showPopup: false,
      validateMsg: "",
      supervisorNotFound: false,
      form: {
        stdInfo: {},
        관리감독자정보: {
          사업장명: "",
          부서명: "",
          직무: "",
          직급: "",
          이름: "",
          사번: "",
          서명: "",
          postId: null,
          boardId: null,
        },
        점검일: "",
        점검시간: "",
        작성일: new Date().format("yyyy-MM-dd"),
        관리감독자점검항목: JSON.parse(JSON.stringify(관리감독자점검항목)),
        작업시작전점검사항: JSON.parse(JSON.stringify(작업시작전점검사항)),
        photoList: [],
        fileList: [],
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key] !== undefined && value[key] !== null) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  async created() {
    const keys = Object.keys(this.form);
    for (const key of keys) {
      if (this.value[key] !== undefined && this.value[key] !== null) {
        this.form[key] = this.value[key];
      }
    }
    const query = this.$route && this.$route.query;
    if (query && query.connect738PostId && !this.form.관리감독자정보.이름) {
      // 1141 모니터링에서 ● 클릭 시 관리감독자 자동 선택
      await this.loadSupervisorFromPost(query.connect738PostId);
    } else if (this.isNew && !this.form.관리감독자정보.이름) {
      // 일반 신규 작성: 로그인한 사용자 기본정보 + 서명 자동 입력
      await this.loadCurrentUserInfo();
    }

    // 신규 작성 시 점검일/점검시간 현재 시각 자동 입력
    if (this.isNew) {
      const now = new Date();
      if (!this.form.점검일) {
        this.form.점검일 = now.format("yyyy-MM-dd");
      }
      if (!this.form.점검시간) {
        const h = String(now.getHours()).padStart(2, "0");
        const m = String(now.getMinutes()).padStart(2, "0");
        this.form.점검시간 = `${h}:${m}`;
      }
    }

    // 1141 쿼리의 점검일로 덮어쓰기
    if (query && query.점검일) {
      this.form.점검일 = query.점검일;
    }
  },
  methods: {
    validate() {
      if (this.supervisorNotFound) {
        this.validateMsg = "관리감독자 정보를 확인할 수 없습니다. 관리감독자 정보를 불러와 주세요.";
        return "write-custom-alert";
      }

      if (!this.form.관리감독자정보.이름) {
        this.validateMsg = "관리감독자 정보를 불러와 주세요.";
        return "write-custom-alert";
      }

      if (!this.form.점검일) {
        this.validateMsg = "점검일을 입력해주세요.";
        return "write-custom-alert";
      }

      if (!this.form.관리감독자정보.서명) {
        this.validateMsg = "서명을 입력해주세요.";
        return "write-custom-alert";
      }

      const uncheckedItem = this.form.관리감독자점검항목.find(function (item) {
        return item.rowType !== "textarea" && !item.isChecked;
      });
      if (uncheckedItem) {
        this.validateMsg = "관리감독자 점검 항목을 모두 체크해주세요.";
        return "write-custom-alert";
      }

      const NOTE_KEY = "비고(조치사항 기재)";
      const allItems = [...this.form.관리감독자점검항목, ...this.form.작업시작전점검사항];
      const invalid = allItems.find((item) => item.isChecked === "불량" && !item[NOTE_KEY] && item.rowType !== "textarea");
      if (invalid) {
        this.validateMsg = "불량 항목의 비고(조치사항 기재)를 입력해주세요.";
        return "write-custom-alert";
      }

      this.validateMsg = "";
    },
    async loadSupervisorFromPost(postId) {
      try {
        const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: parseInt(this.$route.params.proj_id),
          boardId: 738,
          boardSlug: "board-738",
          page: 1,
          perPage: 1,
          conditions: [{ key: "id", operator: "=", value: postId }],
        });
        if (res.data && res.data.length > 0) {
          this.onSelectManager(res.data[0]);
        }
      } catch (e) {}
    },
    onSelectManager(data) {
      if (!data) return;
      this.supervisorNotFound = false;
      const body = JSON.parse(data.post_content).body;
      const stdInfo = body.stdInfo || {};
      const 관리감독자정보 = body.관리감독자정보 || {};
      this.form.관리감독자정보.사업장명 = stdInfo.field_name || "";
      this.form.관리감독자정보.부서명 = 관리감독자정보.부서명 || "";
      this.form.관리감독자정보.직무 = 관리감독자정보.직무 || "";
      this.form.관리감독자정보.직급 = 관리감독자정보.직급 || "";
      this.form.관리감독자정보.이름 = 관리감독자정보.이름 || "";
      this.form.관리감독자정보.사번 = 관리감독자정보.사번 || "";
      this.form.관리감독자정보.서명 = "";
      this.form.관리감독자정보.postId = data.id || null;
      this.form.관리감독자정보.boardId = data.board_id || null;
      this.loadSignatureByNickname(관리감독자정보.사번);
    },

    async loadCurrentUserInfo() {
      const userInfo = this.$store.state.auth.userInfo;
      if (!userInfo || !userInfo.id) return;
      try {
        const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: parseInt(this.$route.params.proj_id),
          boardId: 738,
          boardSlug: "board-738",
          page: 1,
          perPage: 1,
          conditions: [{ key: "body.관리감독자정보.사번", operator: "=", value: userInfo.nickname }],
        });
        if (res.data && res.data.length > 0) {
          this.onSelectManager(res.data[0]);
          return;
        }
      } catch (e) { /* ignore */ }
      // board 738에 현재 사용자 문서 없음 - 서명 로드 없음
      this.supervisorNotFound = true;
    },

    async loadSignatureByUserId(userId) {
      if (!userId) return;
      try {
        const res = await this.$store.dispatch("LOAD_USER_SIGNATURE_BY_ID", { userId });
        const url = (res.file && (res.file.public_url || res.file.path)) || res.signature_file_url || "";
        if (url) this.form.관리감독자정보.서명 = url;
      } catch (e) { /* 서명 조회 실패 시 무시 */ }
    },

    async loadSignatureByNickname(사번) {
      if (!사번) return;
      try {
        const res = await this.$store.dispatch("LOAD_USER_SIGNATURE_BY_NICKNAME", { nickname: 사번 });
        const url = (res.file && (res.file.public_url || res.file.path)) || res.signature_file_url || "";
        if (url) this.form.관리감독자정보.서명 = url;
      } catch (e) { /* 서명 조회 실패 시 무시 */ }
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep .content > div > p:empty {
  display: none;
}
.validate-msg {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #f05650;
  font-weight: 500;
}
.validate-msg-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f05650;
  color: white;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
</style>
